/// <amd-module name="WorkItemsHub/Utils/WorkItemHtmlTableFormatter" />
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { HtmlTableFormatter, IHtmlTableFormatterColumn } from "WorkItemsHub/Utils/HtmlTableFormatter";
import * as Utils_String from 'VSS/Core/Util/String';
import { convertWorkItemIdentityRefFromFieldValue, isWorkItemIdentityRef, isIdentityRef } from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { WorkItemIdentityRef } from "Wit/Identity/Interfaces/WorkItemInterfaces";
import { IVssPageContext } from 'VSS/Platform/Context';
import { ITfsPageService, IProjectData } from 'Tfs/Platform/Page';
import { getEditWorkItemUrl } from "WorkItemsHub/Utils/UrlUtils";
import { convertValueToDisplayString } from "Wit/Common/Utilities/Core";

export enum WorkItemHtmlTableFormatType {
    PlainText = 0,
    Date = 1,
    Identity = 2
}

/**
 * Interface for a work item HTML table formatter column
 */
export interface IWorkItemHtmlTableFormatterColumn extends IHtmlTableFormatterColumn {
    /**
     * Field reference name of the column
     */
    fieldReferenceName: string;

    /**
     * Format type for the column
     */
    formatType: WorkItemHtmlTableFormatType
}

/**
 * Work Item HTML table formatter that takes in columns and list of row values
 */
export class WorkItemHtmlTableFormatter extends HtmlTableFormatter {
    private _pageContext: IVssPageContext;

    constructor(rows: string[][], columns: IWorkItemHtmlTableFormatterColumn[], pageContext: IVssPageContext) {
        super(rows, columns.map(c => ({ ...c, isValueHtml: WorkItemHtmlTableFormatter.isHtmlField(c.fieldReferenceName) } as IWorkItemHtmlTableFormatterColumn)));

        this._pageContext = pageContext;
    }

    protected _getCellValue(rowIndex: number, column: IWorkItemHtmlTableFormatterColumn): string {
        const rowValue = this._rows[rowIndex][column.index];

        if (Utils_String.equals(column.fieldReferenceName, CoreFieldRefNames.Id, true)) {
            return WorkItemHtmlTableFormatter.getIdColumnValue(parseInt(rowValue, 10), this._pageContext);
        }

        let columnFormatType = column.formatType;
        if (columnFormatType === undefined || columnFormatType === null) {
            columnFormatType = WorkItemHtmlTableFormatter._getColumnFormatType(rowValue);
        }

        if (columnFormatType === WorkItemHtmlTableFormatType.Identity) {
            return WorkItemHtmlTableFormatter.getIdentityValue(this._pageContext, rowValue);
        }

        if (columnFormatType === WorkItemHtmlTableFormatType.Date) {
            return convertValueToDisplayString(new Date(rowValue));
        }

        return convertValueToDisplayString(rowValue);
    }

    public static getIdentityValue(pageContext: IVssPageContext, name: string | WorkItemIdentityRef): string {
        const witIdentityRef = convertWorkItemIdentityRefFromFieldValue(pageContext, name);
        return witIdentityRef.identityRef.displayName || "";
    }

    public static getIdColumnValue(workItemId: number, pageContext: IVssPageContext): string {
        const tfsPageService = pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        const project: IProjectData = tfsPageData.project;

        const url = getEditWorkItemUrl(pageContext, project.name, workItemId);
        return `<a href="${url}" style="color:#106ebe" target="_blank">${workItemId}</a>`;
    }

    private static isHtmlField(fieldReferenceName: string): boolean {
        return Utils_String.equals(CoreFieldRefNames.Id, fieldReferenceName, true);
    }

    private static _getColumnFormatType(fieldValue: any): WorkItemHtmlTableFormatType {
        if (isWorkItemIdentityRef(fieldValue) || isIdentityRef(fieldValue)) {
            return WorkItemHtmlTableFormatType.Identity;
        }

        if (fieldValue instanceof Date) {
            return WorkItemHtmlTableFormatType.Date;
        }

        return WorkItemHtmlTableFormatType.PlainText;
    }
}
